import { useState, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import { getStudyState, saveStudyState } from '../services/studyPlanStore';
import { generatePlan } from '../services/onboardingPlan';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { StudyPlan, Task } from '../types/onboarding';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isFeatureEnabled } from '../config/featureFlags';
import { getPragueDate } from '../utils/pragueDate';

export const useStudyPlanManager = () => {
    const { currentUser } = useAuth();
    const [isProcessing, setIsProcessing] = useState(false);

    const checkForNewDay = useCallback(async (forceNextDay: boolean = false) => {
        if (!currentUser) return;

        // Feature flag check - only run if onboarding wizard (new study plan) is enabled
        // We can't access feature flags comfortably here without a hook or helper, 
        // but we can assume we only call this if needed.
        // For safety, we can check basic entitlement or just proceed.

        setIsProcessing(true);
        try {
            const state = await getStudyState(currentUser.uid);
            if (!state || !state.studyPlan) {
                console.log('[StudyPlanManager] No active plan found');
                return;
            }

            // Get mission state from plan (source of truth) with fallback to top-level
            const missionState = state.studyPlan.missionState || state.missionState;
            if (!missionState) {
                console.log('[StudyPlanManager] No mission state found');
                return;
            }

            const lastGenerated = new Date(missionState.lastGeneratedAt);
            const now = new Date();

            // Check if it's a different calendar day using Prague timezone
            const lastGeneratedPrague = getPragueDate(lastGenerated);
            const todayPrague = getPragueDate(now);

            const isNewDay = lastGeneratedPrague !== todayPrague;

            if (isNewDay || forceNextDay) {
                console.log(`[StudyPlanManager] Generating mission for next day (NewDay: ${isNewDay}, Force: ${forceNextDay})`);

                // 1. Calculate new parameters
                // Increment day index
                const currentDayIndex = missionState.dayIndex;
                const newDayIndex = currentDayIndex + 1;

                // Create a temporary mission state for generation that reflects the "previous" state
                // but passing the *new* day index is tricky because generatePlan uses it as "current".
                // Actually generatePlan takes "existingMissionState" to determine rotation.
                // It uses existingMissionState.dayIndex as the current one. 
                // We probably want to pass the OLD state, but modify the dayIndex in the RESULT or allow generatePlan to handle it?
                // Looking at generatePlan: "const dayIndex = existingMissionState?.dayIndex || 1;"
                // So we need to manually increment it in the state we pass, OR update the result.

                // Let's modify the state passed to generatePlan to trick it into generating the next day
                const nextDayState = {
                    ...missionState,
                    dayIndex: newDayIndex
                };

                // Archive current mission to history
                const currentHistory = state.studyPlan.history || [];
                // Only archive if meaningful? For now, we archive everything.
                // We might want to stamp it with "completed" status or date if not present?
                // The todayMission has 'tasks' which should have their completion status... 
                // BUT wait, tasks in `todayMission` are static definitions. Use `missionState.completedTaskIds` to hydrate?
                // The `todayMission` object in Firestore is just the plan. 
                // The completion status is in `missionState`.
                // If we want to show completed items in history, we should arguably update the tasks in the archived mission 
                // to reflect their completion state, OR we rely on a smart component that looks up history status?
                // Simpler: Just archive the mission as is. The history viewer can check generic completion? 
                // Actually, `MissionTaskCard` checks `isTaskCompleted` which looks at local storage or day-based records.
                // If we move to a new day, `completedTaskIds` in missionState is reset!
                // So we MUST burn the completion status into the archived mission tasks OR store a separate history of completions.
                // For MVP: Let's assume we just want to see the *tasks*. 
                // If the user wants to "complete" them retroactively, they can. 
                // If we want to show what was done yesterday, we need to know what was done.
                // `missionState.completedTaskIds` has the IDs.
                // Let's attach completion status to the tasks? No, Task interface doesn't have it.
                // Let's just push "todayMission" to history.
                // If the user goes back to "Yesterday", they see the tasks. 
                // If they completed them, they are recorded in `missionTaskCompletion` service (localStorage or Firestore).
                // `areAllTasksCompleted` uses the plan. 

                // CRITICAL: `missionTaskCompletion` might be day-agnostic or day-specific?
                // `isTaskCompleted` checks `completedTaskIds` from `missionState`.
                // If `missionState` resets, then `isTaskCompleted` will return false for yesterday's tasks!
                // We need a persistent record of completed tasks across days if we want history to show ticks.
                // `services/missionTaskCompletion.ts` uses `missionState.completedTaskIds`.
                // If we reset `missionState.completedTaskIds` for the new day, we lose yesterday's progress visually.

                // SOLUTION: Use the `history` logic to store the *completed* state?
                // Or just keep `completedTaskIds` growing?
                // The `MissionState` type has `completedTaskIds`. If we clear it, we lose it.
                // BUT `MissionState` also has `completedChapters`.

                // Let's look at `missionTaskCompletion.ts` (I don't have it open, but I can guess).
                // If I clear `completedTaskIds`, I lose distinct task completion.
                // Maybe I should NOT clear it? But task IDs might be reused?
                // Task IDs are like `osp-practice-1-0`. They contain the day index.
                // So they are unique per day!
                // `study-1`, `study-2`. unique.
                // SO: We DO NOT need to clear `completedTaskIds`!
                // We can let it grow. It's just strings.
                // Be careful about size? A year of tasks = 365 * 3 = 1000 strings. Small.

                // So, in `getNewDay`, I should NOT clear `completedTaskIds`.
                // However, the `generatePlan` function creates a NEW default `missionState` which resets it.
                // I need to override that.

                const updatedHistory = [...currentHistory, state.studyPlan.todayMission];

                const isPro = await isUserPro(currentUser); // Need a helper for this, or pass it in

                // Identify uncompleted tasks from previous mission to carry over
                // FIX: Check both Firestore state AND local storage to avoid race conditions
                // where a task was just finished but not yet synced to missionState.completedTaskIds
                // Dynamic import to avoid circular dependency issues if any
                const { loadCompletedTasks } = await import('../services/missionTaskCompletion');
                const localProgress = loadCompletedTasks();

                // Create a master set of completed IDs
                const completedIds = new Set([
                    ...(missionState.completedTaskIds || []),
                    ...localProgress.tasks.filter(t => t.completed).map(t => t.taskId)
                ]);

                const uncompletedTasks = (state.studyPlan.todayMission?.tasks || []).filter(t => !completedIds.has(t.id));

                const bonusTasks: Task[] = uncompletedTasks.map(t => ({
                    ...t,
                    id: `bonus-${t.id}`,
                    label: `(Bonus) ${t.label}`
                }));

                // We'll trust the plan generation to do the right thing with rotation if we pass the modified state
                const { plan: newPlan } = generatePlan(state.onboardingData, isPro, nextDayState);

                // Attach bonus tasks to new mission
                if (bonusTasks.length > 0) {
                    newPlan.todayMission.bonusTasks = bonusTasks;
                }

                // 2. Save the new plan

                // Preserve `completedTaskIds` from previous state so history remains checked
                // But we must also include the LOCALLY completed ones that triggered the "not uncompleted" logic above!
                const previousCompletedIds = Array.from(completedIds);

                // Merge completed IDs (new plan has empty list usually)
                const mergedCompletedIds = Array.from(new Set([...previousCompletedIds, ...(newPlan.missionState?.completedTaskIds || [])]));

                // Update new plan with history and merged completion tokens
                newPlan.history = updatedHistory;
                if (newPlan.missionState) {
                    newPlan.missionState.completedTaskIds = mergedCompletedIds;
                    // Update lastGeneratedAt to Prague time ISO (or keep UTC as ISO is UTC, but logic uses Prague date)
                    // Actually generatePlan probably sets it to ISO string. 
                    // ensure consistency if needed, but generatePlan default is likely new Date().toISOString()
                }

                // We don't need to clear task completions anymore since IDs are unique per day
                // const { clearTaskCompletions } = await import('../services/missionTaskCompletion');
                // clearTaskCompletions();

                await saveStudyState(currentUser.uid, {
                    studyPlan: newPlan,
                    missionState: newPlan.missionState
                });

                console.log('[StudyPlanManager] New day mission generated:', newPlan.todayMission.label);

                // Dispatch event to notify widgets
                window.dispatchEvent(new Event('studyPlanUpdated'));
            }
        } catch (e) {
            console.error('[StudyPlanManager] Error checking for new day:', e);
        } finally {
            setIsProcessing(false);
        }
    }, [currentUser]);

    // Helper to simulate time travel (Next Day)
    const simulateNextDay = useCallback(async () => {
        await checkForNewDay(true);
    }, [checkForNewDay]);

    return {
        checkForNewDay,
        simulateNextDay,
        isProcessing
    };
};

// Simple helper for PRO check (mock logic or import real one if possible)
// Since this is a hook, we might not have access to the full entitlement context easily unless we use it.
// For now, defaulting to checking the user object or safe default.
const isUserPro = async (user: any) => {
    // In a real app, we check claims or DB. 
    // For this MVP hook, let's assume false or try to read from local storage / cached claims?
    // Better: Retrieve it from getStudyState if we stored it? No, that's not reliable.
    // Let's just default false for safety, or check if we can import logic.
    return false; // TODO: Connect to real entitlement check if critical for generation
};
